import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthShell, AuthFooter, UnlockOverlay, EyeToggle } from "@/components/brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/admin-login`, { email, password });
      const { token, email: userEmail, role } = response.data;
      localStorage.setItem("token", token);
      localStorage.setItem("email", userEmail);
      localStorage.setItem("role", role);
      setUnlocking(true);
      setTimeout(() => {
        if (role === "admin") navigate("/admin");
        else if (role === "city_official") navigate("/city");
      }, 1800);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {unlocking && <UnlockOverlay label="Unlocking Admin Access" />}

      <AuthShell
        eyebrow="Staff Access"
        title="Staff & City Portal"
        after={
          <p className="text-center mt-6">
            <button onClick={() => navigate("/")} className="text-xs text-mute hover:text-ink transition-colors" data-testid="back-to-client-login">
              Back to client login
            </button>
          </p>
        }
        footer={<AuthFooter>Equal &copy; 2025</AuthFooter>}
      >
        {/* City Official callout */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-[10px] bg-cream border border-[#e6dfd0]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-[8px] flex-shrink-0 flex items-center justify-center bg-white border border-line">
              <svg className="w-4 h-4 text-equal-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
              </svg>
            </div>
            <div>
              <p className="text-ink text-xs font-semibold">City of Long Beach Officials</p>
              <p className="text-mute text-xs mt-0.5">Use your city credentials to access session reports</p>
            </div>
          </div>
          <button onClick={() => navigate("/city-login")}
            className="flex-shrink-0 text-xs text-equal-dark hover:text-ink transition-colors underline underline-offset-2">
            City login →
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            autoComplete="email"
            className="input-flat h-12"
            data-testid="admin-email-input"
          />
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="input-flat h-12 pr-11"
              data-testid="admin-password-input"
            />
            <EyeToggle shown={showPassword} onClick={() => setShowPassword(!showPassword)} />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full h-11 mt-2"
            data-testid="admin-login-button"
          >
            {loading ? "Authenticating..." : "Access Portal"}
          </button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-line" />
          <span className="text-mute text-xs font-mono">or</span>
          <div className="flex-1 h-px bg-line" />
        </div>

        {/* First-time setup */}
        <div className="rounded-[10px] p-4 text-center bg-mint border border-[#cfe9db]">
          <p className="text-mute text-xs mb-3">Logging in for the first time?</p>
          <button
            type="button"
            onClick={() => navigate("/forgot-password?setup=true")}
            className="btn-ghost w-full h-9 text-equal-dark border-equal/40 hover:border-equal hover:bg-white"
            data-testid="first-time-setup-button"
          >
            Create your login
          </button>
          <p className="text-mute text-xs mt-2 leading-relaxed">
            Enter your email and we'll send you a link to set your password
          </p>
        </div>
      </AuthShell>
    </>
  );
}
